/**
 * Document-download availability rules.
 *
 * Menu card is a box-event document; dish label and timestamp are
 * buffet-event documents (box forms — mini buffet / treat box / side box /
 * gathering box — all normalize to `mini_buffet`). A document is therefore
 * unavailable when the selection contains no event that would produce it
 * (GRAIN-1489, GRAIN-2302, GRAIN-2299):
 *   - Menu card needs ≥1 menu-card-eligible box event → disabled otherwise.
 *   - Dish label needs ≥1 buffet event   → disabled when no buffet is selected.
 *   - Timestamp needs ≥1 buffet event    → disabled when no buffet is selected.
 *   - Display copy is the guest-facing bundle (buffet → timestamp + dish label;
 *     mini buffet → menu card) → disabled only when the selection has neither a
 *     buffet nor a menu-card-eligible box event. An ineligible box event (Meal
 *     Box etc.) contributes no page, same as it contributes no standalone card.
 *   - A mix of buffet + eligible box enables all four; an unrelated type (e.g.
 *     meals_on_demand) produces none, so on its own it disables all four.
 *   - Production-and-Fulfilment is always available.
 */

/** Document types offered in the Select documents modal / row Generate menu. */
export const DOCUMENT_KEYS = [
  'production_and_fulfilment',
  'display_copy',
  'timestamp',
  'dish_label',
  'menu_card',
] as const;

export type DocumentKey = (typeof DOCUMENT_KEYS)[number];

/** Normalized `event_type` values (ApiEvent.event_type) the rules distinguish. */
const BUFFET_EVENT_TYPE = 'buffet';
const BOX_EVENT_TYPE = 'mini_buffet';

export const MENU_CARD_PRODUCT_LINES: readonly string[] = [
  'mini_breakfast_buffet',
  'mini_high_tea_buffet',
  'lunch_mini_buffet',
  'dinner_mini_buffet',
];

export interface SelectedEventDescriptor {
  eventType: string;
  productLine?: string | null;
}

export interface DocumentAvailability {
  disabled: boolean;
  /** i18n key (relative to `download_modal`) explaining the disabled state. */
  reasonKey?: string;
}

function printsMenuCard({ eventType, productLine }: SelectedEventDescriptor): boolean {
  if (eventType !== BOX_EVENT_TYPE) return false;
  if (!productLine) return true;
  return MENU_CARD_PRODUCT_LINES.includes(productLine);
}

/**
 * Compute the availability of every document tile for a set of selected events.
 */
export function getDocumentAvailability(
  selectedEvents: readonly SelectedEventDescriptor[]
): Record<DocumentKey, DocumentAvailability> {
  const hasBuffet = selectedEvents.some((event) => event.eventType === BUFFET_EVENT_TYPE);
  const hasEligibleBox = selectedEvents.some(printsMenuCard);

  const menuCardDisabled = !hasEligibleBox;
  // Dish label and timestamp are both buffet-only documents, so they share
  // this gate. Split them if their rules ever diverge.
  const noBuffet = !hasBuffet;
  // Display copy resolves to a buffet or menu-card-eligible box document, so it
  // needs at least one of those; a selection of only ineligible box events
  // (e.g. all Meal Box) contributes nothing, same as Menu card alone.
  const displayCopyDisabled = !hasBuffet && !hasEligibleBox;

  return {
    production_and_fulfilment: { disabled: false },
    display_copy: displayCopyDisabled
      ? { disabled: true, reasonKey: 'display_copy_disabled_reason' }
      : { disabled: false },
    timestamp: noBuffet
      ? { disabled: true, reasonKey: 'timestamp_disabled_reason' }
      : { disabled: false },
    dish_label: noBuffet
      ? { disabled: true, reasonKey: 'dish_label_disabled_reason' }
      : { disabled: false },
    menu_card: menuCardDisabled
      ? { disabled: true, reasonKey: 'menu_card_disabled_reason' }
      : { disabled: false },
  };
}

/**
 * Backend `EventOrders::DocumentType` values each document key expands to. Shared
 * by both the bulk modal and the per-row Generate menu, so wedding routing lives
 * in one place: the Timestamp key emits *both* `timestamp` and `wedding_timestamp`,
 * and Display copy likewise emits *both* `display_copy` and
 * `wedding_display_copy` — the backend `scope_events` routes each event to the
 * right variant (non-wedding → the default type, wedding → the wedding-specific
 * type) and drops empty (type, date) combos. The frontend therefore never needs
 * the event's occasion.
 */
const DOCUMENT_TYPE_EXPANSION: Record<DocumentKey, readonly string[]> = {
  production_and_fulfilment: ['production_and_fulfilment'],
  display_copy: ['display_copy', 'wedding_display_copy'],
  timestamp: ['timestamp', 'wedding_timestamp'],
  dish_label: ['dish_label'],
  menu_card: ['menu_card'],
};

/** Expand chosen document keys into the deduped backend document types to generate. */
export function expandDocumentKeys(keys: readonly DocumentKey[]): string[] {
  const types = new Set<string>();
  for (const key of keys) {
    for (const type of DOCUMENT_TYPE_EXPANSION[key]) types.add(type);
  }
  return [...types];
}
